<template>
  <popup
    v-model="showPopup"
    :close-on-click-out="false"
    :title="t('title', { zone })"
    width="850"
    no-padding
    hide-footer
  >
    <div class="capacity-check-notification">
      <div class="capacity-check-notification__wrap-content">
        <div class="capacity-check-notification__row">
          <p class="p1">
            {{ capacity }} <strong>{{ zone }}</strong>
          </p>
        </div>

        <div class="capacity-check-notification__column">
          <p class="p1">
            {{ t("whatIs") }}
          </p>

          <ul>
            <li>{{ t("list.retry") }}</li>
            <li>{{ t("list.continue") }}</li>
            <li>{{ t("list.cancel") }}</li>
          </ul>
        </div>

        <div v-if="reservedApps.length" class="capacity-check-notification__column">
          <span
            :class="[
              'capacity-check-notification__reserve-status',
              'capacity-check-notification__label',
              'capacity-check-notification__label--success'
            ]"
          >
            <img src="@/assets/cloud-success.svg" alt="Cloud Success" />
            {{ t("type.reserved") }}
          </span>

          <ul class="capacity-check-notification__reserve-list">
            <li v-for="app in reservedApps" :key="app.id" class="capacity-check-notification__reserve-item">
              <capacity-item :app="app" />
            </li>
          </ul>
        </div>

        <div v-if="onDemandApps.length" class="capacity-check-notification__column">
          <span
            :class="[
              'capacity-check-notification__reserve-status',
              'capacity-check-notification__label',
              'capacity-check-notification__label--warning'
            ]"
          >
            <img src="@/assets/cloud-warning.svg" alt="Cloud Success" />
            {{ t("type.onDemand") }}
          </span>

          <ul class="capacity-check-notification__reserve-list">
            <li v-for="app in onDemandApps" :key="app.id" class="capacity-check-notification__reserve-item">
              <capacity-item :app="app" />
            </li>
          </ul>
        </div>
      </div>

      <div class="capacity-check-notification__footer">
        <div class="capacity-check-notification__row">
          <v-btn @click="doAction('cancel')" text large>
            {{ t("buttons.cancel") }}
          </v-btn>
        </div>

        <div class="capacity-check-notification__row">
          <v-btn @click="doAction('retry')" large>
            <v-icon left>action_refresh</v-icon>
            {{ t("buttons.retry") }}
          </v-btn>

          <v-btn @click="doAction('continue')" large>
            <v-icon left>action_upload_to_cloud</v-icon>
            {{ t("buttons.continue") }}
          </v-btn>
        </div>
      </div>
    </div>
  </popup>
</template>

<script lang="ts">
import { defineComponent, ref, PropType, computed, watch, onBeforeUnmount } from "@vue/composition-api"
import { WorkspaceStatusEnum, WorkspaceTemplate } from "@/types/workspace"
import { useStore } from "@/composable/useStore"
import { DeployAction } from "../../lib/types"
import { createTranslationModule } from "@/plugins/i18n"
import Popup from "@/components/popup/PopupRoot.vue"
import CapacityItem from "./_CapacityItem.vue"
import Loader from "@/utils/loader"
import "../../scss/_capacity-check.scss"

export default defineComponent({
  name: "CapacityCheck",
  props: {
    workspace: {
      type: Object as PropType<WorkspaceTemplate>,
      required: true
    }
  },
  setup(props, ctx) {
    const { spaceModule } = useStore()
    const showPopup = ref(false)
    const mounted = ref(true)
    const loader = Loader()
    const t = createTranslationModule("WorkspaceEdit.capacityCheck")

    const isDeployPending = computed(() => {
      return props.workspace.status === WorkspaceStatusEnum.DeployPending
    })

    const capacity = computed(() => {
      if (reservedApps.value.length && onDemandApps.value.length) return t("capacity.partial")
      if (reservedApps.value.length && !onDemandApps.value.length) return t("capacity.reserved")
      return t("capacity.onDemand")
    })

    const reservedApps = computed(() => {
      const assets = props.workspace?.schema?.assets

      if (assets) {
        return assets.filter(a => {
          return a.capacityReservation?.endDate
        })
      }

      return []
    })

    const onDemandApps = computed(() => {
      const assets = props.workspace?.schema?.assets

      if (assets) {
        return props.workspace.schema.assets.filter(a => {
          return !a.capacityReservation || !a.capacityReservation.endDate
        })
      }

      return []
    })

    const zone = computed(() => {
      return props.workspace.cloudZone
    })

    const doAction = (action: DeployAction["action"]) => {
      loader.run(async () => {
        await spaceModule.deployAction({
          workspaceId: props.workspace.id,
          action: action
        })

        if (action !== "cancel") {
          ctx.emit("update")
        }

        showPopup.value = false
      })
    }

    watch(
      isDeployPending,
      () => {
        if (props.workspace.id && mounted.value) {
          showPopup.value = isDeployPending.value && mounted.value
        }
      },
      { immediate: true }
    )

    onBeforeUnmount(() => {
      mounted.value = false
    })

    return {
      t,
      capacity,
      doAction,
      zone,
      reservedApps,
      onDemandApps,
      isDeployPending,
      showPopup
    }
  },
  components: {
    Popup,
    CapacityItem
  }
})
</script>
